import React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
} from '@tanstack/react-table';
import type { ProgramFeeInvoice } from '../types/report.types';

// Component props
interface ProgramFeesReportTableProps {
  data: ProgramFeeInvoice[];
  columns?: ColumnDef<ProgramFeeInvoice, unknown>[];
  isLoading?: boolean;
  className?: string;
}

export const ProgramFeesReportTable: React.FC<ProgramFeesReportTableProps> = ({
  data,
  columns = programFeesColumns,
  isLoading = false,
  className = '',
}) => {
  const table = useReactTable<ProgramFeeInvoice>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading)
    return (
      <div className="p-4 space-y-2">
        {[...Array(10)].map((_, i) => (
          <div key={i} className="h-10 bg-gray-300 animate-pulse rounded" />
        ))}
      </div>
    );

  return (
    <div
      className={`min-w-3xl ${className}`}
      style={{ scrollbarWidth: 'none' }}
    >
      <table className="w-full">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id} className="p-2 md:p-4 text-sm md:text-base text-center border">
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="p-2 md:p-4 text-sm md:text-base text-start border">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={columns.length}
                className="py-6 text-start text-gray-400"
              >
                No records found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

// Columns definition for Program Fees
const programFeesColumns: ColumnDef<ProgramFeeInvoice>[] = [
  {
    accessorKey: 'invoiceId',
    header: () => <div className="text-center font-bold">Invoice ID</div>,
    cell: (info) => <div className="">{String(info.getValue() ?? '-')}</div>,
  },
  {
    accessorKey: 'residentName',
    header: () => <div className="font-bold">Resident</div>,
    cell: (info) => <div className="">{String(info.getValue() ?? '-')}</div>,
  },
  {
    accessorKey: 'houseName',
    header: () => <div className="font-bold">House</div>,
    cell: (info) => <div className="">{String(info.getValue() ?? '-')}</div>,
  },
  {
    accessorKey: 'date',
    header: () => <div className="text-center font-bold">Date</div>,
    cell: (info) => {
      const date = info.getValue() as string | undefined;
      return (
        <div className="text-center">
          {date
            ? new Date(date).toLocaleDateString('en-US', {
                month: 'short',
                day: '2-digit',
                year: 'numeric',
              })
            : '-'}
        </div>
      );
    },
  },
  {
    accessorKey: 'amount',
    header: () => <div className="text-center font-bold">Amount</div>,
    cell: (info) => (
      <div className="text-center text-green-700">
        ${String(info.getValue() ?? 0)}
      </div>
    ),
  },
  {
    accessorKey: 'paid',
    header: () => <div className="text-center font-bold">Status</div>,
    cell: (info) => {
      const paid = info.getValue() as boolean;
      return (
        <div className="text-center">
          {paid ? (
            <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
              Paid
            </span>
          ) : (
            <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold">
              Unpaid
            </span>
          )}
        </div>
      );
    },
  },
];
